import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OnboardingForm } from '../components/OnboardingForm';
import { useCreateOnboarding, useOnboarding, useUpdateOnboarding } from '../queries';
import type { OnboardingInput } from '../types';

export function OnboardingFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const onboardingQuery = useOnboarding(isEdit ? publicId : -1);
  const create = useCreateOnboarding();
  const update = useUpdateOnboarding();

  async function handleSubmit(values: OnboardingInput) {
    const saved = isEdit
      ? await update.mutateAsync({ id: publicId, input: values })
      : await create.mutateAsync(values);
    navigate(`/hrm/onboarding/${saved.public_id}`);
  }

  if (isEdit && onboardingQuery.isPending) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const onboarding = onboardingQuery.data;

  return (
    <div className="w-full max-w-3xl space-y-8">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/hrm/onboarding/${publicId}` : '/hrm/onboarding')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit checklist' : 'New checklist'}
        </h1>
      </div>

      <OnboardingForm
        submitLabel={isEdit ? 'Save changes' : 'Create checklist'}
        showChecklist={!isEdit}
        defaultValues={
          onboarding
            ? {
                employee: String(onboarding.employee),
                kind: onboarding.kind,
                status: onboarding.status,
                started_on: onboarding.started_on ?? '',
                completed_on: onboarding.completed_on ?? '',
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/hrm/onboarding/${publicId}` : '/hrm/onboarding')}
      />
    </div>
  );
}
